import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, Loader2, PauseCircle, PlayCircle } from 'lucide-react';

import { getAutoCleanup, setAutoCleanupEnabled } from '@/services/api/custom-targeting';
import type { AutoCleanupRun, AutoCleanupState } from '@/types/custom-targeting';
import {
    KEY_STATUS_LABEL,
    RUN_STATUS_LABEL,
    keyCounts,
    orderedKeys,
    runMinutes,
    skippedTotal,
    summarize,
    type AutoCleanupTone,
} from '@/lib/autoCleanup';
import { formatCount, formatPercent, plural } from '@/lib/keyCapacity';
import { formatDateTimeShort, parseApiDate } from '@/lib/apiDate';
import { formatReportDay, apiErrorDetail } from '@/lib/keyCleanup';
import { useNetwork } from '@/contexts/NetworkContext';
import { getCurrentUser } from '@/services/api/auth';

const QUERY_KEY = ['custom-targeting-auto-cleanup'];

const TONE: Record<AutoCleanupTone, { box: string; icon: typeof CheckCircle2 }> = {
    ok: { box: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20', icon: CheckCircle2 },
    running: { box: 'bg-blue-50 text-blue-800 ring-blue-600/20', icon: Loader2 },
    waiting: { box: 'bg-slate-50 text-slate-700 ring-slate-500/20', icon: Clock },
    paused: { box: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: PauseCircle },
    warning: { box: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: AlertTriangle },
    error: { box: 'bg-rose-50 text-rose-800 ring-rose-600/20', icon: AlertTriangle },
};

const RUN_DOT: Record<AutoCleanupRun['status'], string> = {
    running: 'bg-blue-500',
    done: 'bg-emerald-500',
    done_with_errors: 'bg-amber-500',
    failed: 'bg-rose-500',
    interrupted: 'bg-rose-500',
};

const desativados = (n: number) => plural(n, 'desativado', 'desativados');

/**
 * Acompanhamento da limpeza automática diária: rodou hoje? quanto tirou de
 * cada key? A rotina roda sozinha no ms-custom-targeting; aqui só se vê e,
 * para admin, se pausa.
 */
export default function AutoCleanupPanel() {
    const queryClient = useQueryClient();
    const { format } = useNetwork();
    const isAdmin = getCurrentUser()?.role === 'admin';
    const [aberta, setAberta] = useState<string | null>(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => getAutoCleanup(),
        // Rodando, acompanha; parada, não há o que buscar com pressa.
        refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === 'running') ? 15_000 : 5 * 60_000),
        refetchOnWindowFocus: false,
    });

    const alternar = useMutation({
        mutationFn: (enabled: boolean) => setAutoCleanupEnabled(enabled),
        onSuccess: (novo) => queryClient.setQueryData<AutoCleanupState>(QUERY_KEY, novo),
    });

    const resumo = data ? summarize(data, new Date(), desativados) : null;
    const Icone = resumo ? TONE[resumo.tone].icon : Clock;

    return (
        <section className="mb-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-base font-semibold text-slate-900">Limpeza automática</h2>
                    <p className="mt-0.5 text-sm text-slate-500">
                        {data
                            ? `Uma vez por dia, às ${horaLocal(data.hour_utc)}: nas keys de UTM a partir de ${data.min_percent}% do limite, ` +
                              `desativa no GAM os values sem receita há ${data.idle_days} dias (as _hour, que não aparecem no relatório, só por idade). ` +
                              'Value desativado que voltar a chegar é recriado sozinho.'
                            : 'Uma vez por dia desativa no GAM os values sem receita há 7 dias.'}
                    </p>
                </div>
                {isAdmin && data && (
                    <button
                        type="button"
                        onClick={() => alternar.mutate(!data.enabled)}
                        disabled={alternar.isPending}
                        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                        title={data.enabled ? 'Pausar não interrompe uma rodada em andamento' : undefined}
                    >
                        {data.enabled ? <PauseCircle className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />}
                        {data.enabled ? 'Pausar' : 'Religar'}
                    </button>
                )}
            </div>

            {alternar.isError && <p className="mt-2 text-xs text-rose-700">{apiErrorDetail(alternar.error)}</p>}

            {isLoading && <div className="mt-4 h-12 animate-pulse rounded-lg bg-slate-100" />}
            {isError && <p className="mt-4 text-sm text-slate-600">Não foi possível ler a limpeza automática agora.</p>}

            {data && resumo && (
                <>
                    <div className={`mt-4 flex items-start gap-3 rounded-lg p-3 text-sm ring-1 ring-inset ${TONE[resumo.tone].box}`}>
                        <Icone className={`mt-0.5 h-4 w-4 flex-shrink-0 ${resumo.tone === 'running' ? 'animate-spin' : ''}`} />
                        <div>
                            <p className="font-medium">{resumo.title}</p>
                            {!data.enabled && data.updated_by && (
                                <p className="mt-0.5 text-xs opacity-90">
                                    Pausada por {data.updated_by}
                                    {data.updated_at && ` em ${formatDateTimeShort(parseApiDate(data.updated_at))}`}.
                                </p>
                            )}
                            {resumo.today?.error && <p className="mt-0.5 text-xs opacity-90">{resumo.today.error}</p>}
                        </div>
                    </div>

                    {data.runs.length === 0 ? (
                        <p className="mt-4 text-sm text-slate-500">Nenhuma rodada ainda.</p>
                    ) : (
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
                                        <th className="py-2 pr-3 font-medium">Dia</th>
                                        <th className="py-2 pr-3 font-medium">Resultado</th>
                                        <th className="py-2 pr-3 text-right font-medium">Desativados</th>
                                        <th className="py-2 pr-3 font-medium">Keys</th>
                                        <th className="py-2 font-medium">Horário</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.runs.map((r) => (
                                        <Fragment key={r.day}>
                                            <LinhaRodada run={r} aberta={aberta === r.day} onToggle={() => setAberta(aberta === r.day ? null : r.day)} />
                                            {aberta === r.day && (
                                                <tr>
                                                    <td colSpan={5} className="bg-slate-50 px-3 py-3">
                                                        <KeysDaRodada run={r} formatNetwork={format} />
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

function LinhaRodada({ run, aberta, onToggle }: { run: AutoCleanupRun; aberta: boolean; onToggle: () => void }) {
    const c = keyCounts(run);
    const min = runMinutes(run);
    const Seta = aberta ? ChevronDown : ChevronRight;
    return (
        <tr className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={onToggle}>
            <td className="py-2 pr-3 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                    <Seta className="h-3.5 w-3.5 text-slate-400" />
                    {formatReportDay(run.day)}
                </span>
            </td>
            <td className="py-2 pr-3 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5 text-slate-700">
                    <span className={`h-2 w-2 rounded-full ${RUN_DOT[run.status]}`} />
                    {RUN_STATUS_LABEL[run.status]}
                    {run.attempt && run.attempt > 1 && <span className="text-xs text-slate-400">({run.attempt}ª tentativa)</span>}
                </span>
            </td>
            <td className="py-2 pr-3 text-right font-semibold tabular-nums text-slate-900">{formatCount(run.deactivated)}</td>
            <td className="py-2 pr-3 whitespace-nowrap text-xs text-slate-500">
                {c.limpa} limpas · {c.pulada} puladas{c.falhou > 0 && <span className="text-rose-600"> · {c.falhou} com falha</span>}
            </td>
            <td className="py-2 whitespace-nowrap text-xs text-slate-500">
                {formatDateTimeShort(parseApiDate(run.started_at))}
                {min !== null && ` · ${min} min`}
            </td>
        </tr>
    );
}

function KeysDaRodada({ run, formatNetwork }: { run: AutoCleanupRun; formatNetwork: (code: string) => string }) {
    if (run.keys.length === 0) {
        return <p className="text-xs text-slate-500">Nenhuma key processada ainda.</p>;
    }
    return (
        <table className="w-full text-xs">
            <thead>
                <tr className="text-left text-[11px] text-slate-400">
                    <th className="pb-1.5 pr-3 font-medium">Key</th>
                    <th className="pb-1.5 pr-3 font-medium">Conta</th>
                    <th className="pb-1.5 pr-3 font-medium">Resultado</th>
                    <th className="pb-1.5 pr-3 text-right font-medium">Uso antes</th>
                    <th className="pb-1.5 pr-3 text-right font-medium">Desativados</th>
                    <th className="pb-1.5 font-medium">Detalhe</th>
                </tr>
            </thead>
            <tbody>
                {orderedKeys(run).map((k) => (
                    <tr key={`${k.network}-${k.key}`} className="border-t border-slate-200/70 align-top">
                        <td className="py-1.5 pr-3 font-mono text-slate-700">{k.key}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">{formatNetwork(k.network)}</td>
                        <td
                            className={`py-1.5 pr-3 whitespace-nowrap font-medium ${
                                k.status === 'falhou' ? 'text-rose-600' : k.status === 'limpa' ? 'text-emerald-700' : 'text-slate-500'
                            }`}
                        >
                            {KEY_STATUS_LABEL[k.status]}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-slate-600">
                            {k.percent_before !== null && k.percent_before !== undefined ? formatPercent(k.percent_before) : '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-semibold tabular-nums text-slate-900">{formatCount(k.deactivated)}</td>
                        <td className="py-1.5 text-slate-500">
                            {k.status === 'pulada' && k.reason}
                            {k.status === 'falhou' && <span className="text-rose-700">{k.error}</span>}
                            {k.status === 'limpa' && k.skipped && (
                                <span title={`${formatCount(k.scanned)} varridos`}>
                                    mantidos {formatCount(skippedTotal(k.skipped))}: {formatCount(k.skipped.receita ?? 0)} com receita,{' '}
                                    {formatCount(k.skipped.recente)} recentes, {formatCount(k.skipped.na_fila)} na fila
                                </span>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** A hora marcada (UTC) no fuso de quem olha: 7 UTC vira "04:00" em Brasília. */
function horaLocal(hourUtc: number): string {
    const d = new Date();
    d.setUTCHours(hourUtc, 0, 0, 0);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
